import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, Mail, Timer } from "lucide-react";
import { SLOTS_REMAINING, SLOTS_TOTAL, SLOTS_PERIOD } from "@/config/availability";

const CALENDLY_URL = process.env.REACT_APP_CALENDLY_URL;

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export default function Hero() {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-32 md:pt-40 pb-20 md:pb-24"
    >
      {/* background layers */}
      <div className="absolute inset-0 bg-grid pointer-events-none opacity-60" />
      <div className="absolute inset-0 bg-radial-fade pointer-events-none" />
      <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/40 to-transparent" />

      <div className="relative mx-auto max-w-7xl px-6 md:px-12 lg:px-16">
        {/* Industry-targeted kicker */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="flex items-center gap-3 text-xs font-mono uppercase tracking-[0.2em] text-white/60"
        >
          <span className="inline-flex h-1.5 w-1.5 rounded-full bg-[#00E5FF] pulse-dot" />
          <span data-testid="hero-kicker">
            Built for construction · property · professional services
          </span>
        </motion.div>

        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="font-heading mt-8 text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter leading-[0.95] max-w-5xl"
        >
          Find out exactly where AI is{" "}
          <span className="text-[#00E5FF] glow-text">leaking revenue</span> in
          your business.
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.2 }}
          className="mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed"
        >
          A focused diagnostic for construction, property and professional
          services firms. Answer 15 sharp questions, get a personalised AI Audit
          Scorecard delivered to your inbox in under 60 seconds.
        </motion.p>

        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.3 }}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            data-testid="hero-cta-primary"
            onClick={() => scrollTo("audit")}
            className="group inline-flex items-center gap-2 bg-[#00E5FF] text-black font-medium hover:bg-[#00B8CC] transition-all px-8 py-4 uppercase tracking-widest text-sm"
          >
            Get my scorecard
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
          </button>
          <button
            data-testid="hero-cta-secondary"
            onClick={() => scrollTo("how")}
            className="inline-flex items-center gap-2 border border-white/20 text-white hover:bg-white/5 transition-colors px-8 py-4 uppercase tracking-widest text-sm"
          >
            How it works
          </button>
        </motion.div>

        {/* Scarcity / live availability */}
        <motion.a
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.7, delay: 0.4 }}
          href={CALENDLY_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="hero-availability"
          className="mt-6 inline-flex items-center gap-3 group"
        >
          <span className="relative inline-flex h-1.5 w-1.5">
            <span className="absolute inset-0 rounded-full bg-[#00E5FF] pulse-dot" />
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-white/65 group-hover:text-white transition-colors">
            <span className="text-[#00E5FF] font-semibold" data-testid="hero-slots-remaining">
              {SLOTS_REMAINING}
            </span>
            <span className="text-white/35"> / {SLOTS_TOTAL}</span>{" "}
            follow-up slots remaining {SLOTS_PERIOD}
          </span>
          <span className="font-mono text-xs uppercase tracking-[0.18em] text-white/45 group-hover:text-[#00E5FF] transition-colors">
            See availability →
          </span>
        </motion.a>

        {/* Social proof bar */}
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          data-testid="hero-social-proof"
          className="mt-12 border-y border-white/10 py-5 grid grid-cols-2 sm:grid-cols-4 gap-3 text-center sm:text-left"
        >
          <div>
            <div className="font-heading text-3xl md:text-4xl font-bold text-white">
              <span className="text-[#00E5FF]">£63M</span>
            </div>
            <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/50 mt-1">
              New business generated
            </div>
          </div>
          <div className="sm:border-l sm:border-white/10 sm:pl-6">
            <div className="font-heading text-3xl md:text-4xl font-bold text-white">
              80<span className="text-[#00E5FF]">+</span>
            </div>
            <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/50 mt-1">
              AI audits this year
            </div>
          </div>
          <div className="sm:border-l sm:border-white/10 sm:pl-6">
            <div className="font-heading text-3xl md:text-4xl font-bold text-white">
              30<span className="text-[#00E5FF]">+</span>
            </div>
            <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/50 mt-1">
              AI outbound systems installed
            </div>
          </div>
          <div className="sm:border-l sm:border-white/10 sm:pl-6">
            <div className="font-heading text-3xl md:text-4xl font-bold text-white">
              UK<span className="text-[#00E5FF]"> · </span>CW
            </div>
            <div className="font-mono text-[10px] md:text-xs uppercase tracking-widest text-white/50 mt-1">
              Featured AI speaker
            </div>
          </div>
        </motion.div>

        {/* meta strip */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-10 grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/10 border border-white/10"
        >
          {[
            { icon: Timer, label: "5-minute diagnostic", sub: "15 weighted questions" },
            { icon: Mail, label: "PDF delivered by email", sub: "Exec-ready report" },
            { icon: ShieldCheck, label: "Founder-led follow-up", sub: "Faisal will personally respond to you" },
          ].map(({ icon: Icon, label, sub }) => (
            <div
              key={label}
              className="bg-[#050505] px-6 py-5 flex items-center gap-4"
            >
              <div className="h-9 w-9 grid place-items-center border border-white/15 text-[#00E5FF]">
                <Icon className="h-4 w-4" />
              </div>
              <div>
                <div className="text-sm font-medium">{label}</div>
                <div className="text-xs text-white/50 font-mono uppercase tracking-wider mt-0.5">
                  {sub}
                </div>
              </div>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
